import { useState } from "react";
import { ArrowRight, Loader2, Mail, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { CONTACT_EMAIL, EMAIL_URL, SECTION_LABEL, WHATSAPP_URL } from "@/lib/site";
import { track } from "@/lib/track";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
    "AI Strategy & Consulting",
    "AI Workflow Automation",
    "Custom Websites & Web Applications",
    "AI-Powered Business Solutions",
    "Process Optimization & Digital Transformation",
    "White-Label AI Development for Agencies",
    "Other",
];

const BUDGET_OPTIONS = [
    "Under $500",
    "$500–$1,000",
    "$1,000–$2,500",
    "$2,500–$5,000",
    "$5,000+",
];

const EMPTY = {
    full_name: "",
    email: "",
    company: "",
    country: "",
    service: "",
    description: "",
    budget: "",
    timeline: "",
    consent: false,
    website: "", // honeypot
};

const INPUT_CLS =
    "mt-1.5 w-full rounded-lg border border-[#EDE3DA] bg-white px-4 py-2.5 text-sm text-[#1C1917] placeholder:text-[#A89E95] focus:outline-none focus:ring-2 focus:ring-[#CF4242] focus:border-transparent transition-shadow duration-200";
const LABEL_CLS = "block text-sm font-semibold text-[#1C1917]";

export default function Contact() {
    const [form, setForm] = useState(EMPTY);
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState("idle"); // idle | sending | success

    const set = (key, value) => {
        setForm((f) => ({ ...f, [key]: value }));
        setErrors((e) => ({ ...e, [key]: undefined }));
    };

    const validate = () => {
        const e = {};
        if (form.full_name.trim().length < 2) e.full_name = "Please enter your full name.";
        if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Please enter a valid business email.";
        if (!form.company.trim()) e.company = "Please enter your company name.";
        if (!form.country.trim()) e.country = "Please enter your country.";
        if (!form.service) e.service = "Please choose a service.";
        if (form.description.trim().length < 10)
            e.description = "Please describe your project in a few sentences.";
        if (!form.budget) e.budget = "Please choose a budget range.";
        if (!form.timeline.trim()) e.timeline = "Please tell us your desired timeline.";
        if (!form.consent) e.consent = "Please confirm we may contact you about this inquiry.";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const submit = async (ev) => {
        ev.preventDefault();
        if (status === "sending" || status === "success") return;
        if (!validate()) {
            toast.error("Please review the highlighted fields.");
            return;
        }
        setStatus("sending");
        try {
            const res = await fetch(`${API}/inquiries`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(form),
            });
            if (res.status === 409) {
                toast("We have already received this inquiry.", {
                    description: "We'll review it and reply by email.",
                });
                setStatus("success");
                track("form_duplicate", {});
                return;
            }
            if (!res.ok) throw new Error(`Request failed (${res.status})`);
            setStatus("success");
            setForm(EMPTY);
            toast.success("Inquiry sent. We'll review it and reply by email.");
            track("form_submit", { service: "inquiry_sent" });
        } catch {
            setStatus("idle");
            toast.error("Something went wrong while sending. Please try again or email us directly.");
        }
    };

    const fieldError = (key) =>
        errors[key] ? (
            <p className="mt-1 text-xs font-medium text-[#DC2626]" role="alert">
                {errors[key]}
            </p>
        ) : null;

    return (
        <section id="contact" className="relative scroll-mt-20 overflow-hidden bg-[#F7F0EA]" data-testid="contact-section">
            <div
                className="pointer-events-none absolute -right-28 top-10 h-[420px] w-[420px] rounded-full bg-[#CF4242]/[0.08] blur-3xl"
                aria-hidden="true"
            />
            <div className="relative mx-auto max-w-7xl px-6 py-24 lg:px-8 lg:py-32">
                <div className="grid grid-cols-1 gap-14 lg:grid-cols-5 lg:gap-16">
                    <Reveal className="lg:col-span-2 lg:self-start">
                        <p className={SECTION_LABEL}>Contact</p>
                        <h2 className="mt-4 font-heading text-3xl font-medium tracking-tight text-[#1C1917] md:text-4xl lg:text-[3.1rem] lg:leading-[1.1]">
                            Have a project in mind?{" "}
                            <span className="font-serif italic text-[#CF4242]">
                                Let's make it work.
                            </span>
                        </h2>
                        <p className="mt-5 text-base leading-relaxed text-[#57504A] md:text-lg">
                            Tell us what you're building, what needs to improve or which
                            workflow you'd like to automate. We'll review the requirements and
                            discuss a suitable next step.
                        </p>

                        <div className="mt-8 space-y-3">
                            {EMAIL_URL && (
                                <a
                                    href={EMAIL_URL}
                                    data-testid="contact-email-link"
                                    onClick={() => track("cta_click", { cta: "contact_email" })}
                                    className="flex items-center gap-3 rounded-xl border border-[#EDE3DA] bg-white px-5 py-4 text-sm font-semibold text-[#1C1917] shadow-sm transition-colors duration-200 hover:border-[#CF4242]"
                                >
                                    <Mail size={18} className="text-[#CF4242]" aria-hidden="true" />
                                    {CONTACT_EMAIL}
                                </a>
                            )}
                            {WHATSAPP_URL && (
                                <a
                                    href={WHATSAPP_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="contact-whatsapp-link"
                                    onClick={() => track("cta_click", { cta: "contact_whatsapp" })}
                                    className="flex items-center gap-3 rounded-xl border border-[#EDE3DA] bg-white px-5 py-4 text-sm font-semibold text-[#1C1917] shadow-sm transition-colors duration-200 hover:border-[#CF4242]"
                                >
                                    <MessageCircle size={18} className="text-[#CF4242]" aria-hidden="true" />
                                    Chat on WhatsApp
                                </a>
                            )}
                        </div>
                    </Reveal>

                    <Reveal delay={0.1} className="lg:col-span-3">
                        {status === "success" ? (
                            <div
                                className="rounded-[1.5rem] border border-[#EDE3DA] bg-white p-10 shadow-[0_28px_60px_-28px_rgb(28,25,23,0.35)]"
                                role="status"
                                data-testid="contact-success-message"
                            >
                                <h3 className="font-heading text-2xl font-semibold text-[#1C1917]">
                                    Thank you — your inquiry is in.
                                </h3>
                                <p className="mt-3 text-sm leading-relaxed text-[#57504A] md:text-base">
                                    We've received your project details and will review them
                                    carefully. You'll hear back by email to discuss next steps.
                                </p>
                                <button
                                    type="button"
                                    data-testid="contact-send-another-btn"
                                    onClick={() => setStatus("idle")}
                                    className="mt-6 text-sm font-semibold text-[#CF4242] transition-colors duration-200 hover:text-[#A92F31]"
                                >
                                    Send another inquiry
                                </button>
                            </div>
                        ) : (
                            <form
                                onSubmit={submit}
                                noValidate
                                data-testid="contact-form"
                                className="spotlight relative overflow-hidden rounded-[1.5rem] border border-[#EDE3DA] bg-white p-8 shadow-[0_28px_60px_-28px_rgb(28,25,23,0.35)] sm:p-10"
                            >
                                <span
                                    className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-[#CF4242] via-[#E8764A] to-[#CF4242]"
                                    aria-hidden="true"
                                />
                                {/* Honeypot: invisible to humans, attractive to bots */}
                                <div className="absolute left-[-9999px] top-[-9999px]" aria-hidden="true">
                                    <label htmlFor="website">Website</label>
                                    <input
                                        id="website"
                                        name="website"
                                        type="text"
                                        tabIndex={-1}
                                        autoComplete="off"
                                        value={form.website}
                                        onChange={(e) => set("website", e.target.value)}
                                    />
                                </div>

                                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                    <div>
                                        <label htmlFor="full_name" className={LABEL_CLS}>
                                            Full name <span aria-hidden="true" className="text-[#DC2626]">*</span>
                                        </label>
                                        <input
                                            id="full_name"
                                            data-testid="contact-fullname-input"
                                            type="text"
                                            autoComplete="name"
                                            className={INPUT_CLS}
                                            value={form.full_name}
                                            onChange={(e) => set("full_name", e.target.value)}
                                            aria-invalid={!!errors.full_name}
                                            required
                                        />
                                        {fieldError("full_name")}
                                    </div>
                                    <div>
                                        <label htmlFor="email" className={LABEL_CLS}>
                                            Business email <span aria-hidden="true" className="text-[#DC2626]">*</span>
                                        </label>
                                        <input
                                            id="email"
                                            data-testid="contact-email-input"
                                            type="email"
                                            autoComplete="email"
                                            placeholder="you@company.com"
                                            className={INPUT_CLS}
                                            value={form.email}
                                            onChange={(e) => set("email", e.target.value)}
                                            aria-invalid={!!errors.email}
                                            required
                                        />
                                        {fieldError("email")}
                                    </div>
                                    <div>
                                        <label htmlFor="company" className={LABEL_CLS}>
                                            Company name <span aria-hidden="true" className="text-[#DC2626]">*</span>
                                        </label>
                                        <input
                                            id="company"
                                            data-testid="contact-company-input"
                                            type="text"
                                            autoComplete="organization"
                                            className={INPUT_CLS}
                                            value={form.company}
                                            onChange={(e) => set("company", e.target.value)}
                                            aria-invalid={!!errors.company}
                                            required
                                        />
                                        {fieldError("company")}
                                    </div>
                                    <div>
                                        <label htmlFor="country" className={LABEL_CLS}>
                                            Country <span aria-hidden="true" className="text-[#DC2626]">*</span>
                                        </label>
                                        <input
                                            id="country"
                                            data-testid="contact-country-input"
                                            type="text"
                                            autoComplete="country-name"
                                            placeholder="e.g. India"
                                            className={INPUT_CLS}
                                            value={form.country}
                                            onChange={(e) => set("country", e.target.value)}
                                            aria-invalid={!!errors.country}
                                            required
                                        />
                                        {fieldError("country")}
                                    </div>
                                    <div>
                                        <label htmlFor="service" className={LABEL_CLS}>
                                            Service required <span aria-hidden="true" className="text-[#DC2626]">*</span>
                                        </label>
                                        <select
                                            id="service"
                                            data-testid="contact-service-select"
                                            className={INPUT_CLS}
                                            value={form.service}
                                            onChange={(e) => set("service", e.target.value)}
                                            aria-invalid={!!errors.service}
                                            required
                                        >
                                            <option value="">Select a service</option>
                                            {SERVICE_OPTIONS.map((o) => (
                                                <option key={o} value={o}>{o}</option>
                                            ))}
                                        </select>
                                        {fieldError("service")}
                                    </div>
                                    <div>
                                        <label htmlFor="budget" className={LABEL_CLS}>
                                            Budget range <span aria-hidden="true" className="text-[#DC2626]">*</span>
                                        </label>
                                        <select
                                            id="budget"
                                            data-testid="contact-budget-select"
                                            className={INPUT_CLS}
                                            value={form.budget}
                                            onChange={(e) => set("budget", e.target.value)}
                                            aria-invalid={!!errors.budget}
                                            required
                                        >
                                            <option value="">Select a range</option>
                                            {BUDGET_OPTIONS.map((o) => (
                                                <option key={o} value={o}>{o}</option>
                                            ))}
                                        </select>
                                        {fieldError("budget")}
                                    </div>
                                    <div className="sm:col-span-2">
                                        <label htmlFor="description" className={LABEL_CLS}>
                                            Project description <span aria-hidden="true" className="text-[#DC2626]">*</span>
                                        </label>
                                        <textarea
                                            id="description"
                                            data-testid="contact-description-input"
                                            rows={4}
                                            placeholder="What are you building, and what should it achieve?"
                                            className={`${INPUT_CLS} resize-y`}
                                            value={form.description}
                                            onChange={(e) => set("description", e.target.value)}
                                            aria-invalid={!!errors.description}
                                            required
                                        />
                                        {fieldError("description")}
                                    </div>
                                    <div className="sm:col-span-2">
                                        <label htmlFor="timeline" className={LABEL_CLS}>
                                            Desired timeline <span aria-hidden="true" className="text-[#DC2626]">*</span>
                                        </label>
                                        <input
                                            id="timeline"
                                            data-testid="contact-timeline-input"
                                            type="text"
                                            placeholder="e.g. within 2 months, flexible, urgent"
                                            className={INPUT_CLS}
                                            value={form.timeline}
                                            onChange={(e) => set("timeline", e.target.value)}
                                            aria-invalid={!!errors.timeline}
                                            required
                                        />
                                        {fieldError("timeline")}
                                    </div>
                                </div>

                                <div className="mt-6">
                                    <label className="flex items-start gap-3 text-sm text-[#57504A]">
                                        <input
                                            type="checkbox"
                                            data-testid="contact-consent-checkbox"
                                            className="mt-1 h-4 w-4 rounded border-[#EDE3DA] accent-[#CF4242]"
                                            checked={form.consent}
                                            onChange={(e) => set("consent", e.target.checked)}
                                            aria-invalid={!!errors.consent}
                                        />
                                        <span>
                                            I agree to be contacted by StackDino about
                                            this inquiry. My details are used only to respond to
                                            this request.
                                        </span>
                                    </label>
                                    {fieldError("consent")}
                                </div>

                                <button
                                    type="submit"
                                    data-testid="contact-submit-btn"
                                    disabled={status === "sending"}
                                    className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#CF4242] px-6 py-3 text-sm font-semibold text-white shadow-sm transition-colors duration-200 hover:bg-[#A92F31] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#CF4242] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                                >
                                    {status === "sending" ? (
                                        <>
                                            <Loader2 size={16} className="animate-spin" aria-hidden="true" />
                                            Sending…
                                        </>
                                    ) : (
                                        <>
                                            Send Inquiry <ArrowRight size={16} aria-hidden="true" />
                                        </>
                                    )}
                                </button>
                            </form>
                        )}
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
